import type { StockComponent, StockComponentWriteItem } from '@/types/oscar';

/**
 * One row being edited. Mirrors StockComponentWriteItem, but every field is
 * present and non-null so the inputs stay controlled.
 *
 * ⚠ `key` is a CLIENT-SIDE identity, not StockItemComponentId. A new row has
 * no server id, and React needs a stable key before the first save or every
 * keystroke re-mounts the row and the caret jumps to the end.
 */
export interface PartDraft {
  key: string;
  ComponentStockItemId: number | null;
  Description: string;
  SupplierPartNumber: string;

  /**
   * ⛔ '' IS A LEGITIMATE STATE WHILE TYPING, and leaving it out was a real
   * defect: coercing an empty box back to 1 on every keystroke meant clearing
   * the box and typing "2" produced TWELVE. The box could not be edited at
   * all, only appended to.
   *
   * ⚠ Coerced to a number on the way OUT instead (toWrite), where the
   * database's "must be at least one" actually applies.
   */
  Qty: number | '';
  /** Carried through from the server so a linked row can show what it points at. */
  LinkedName: string | null;
  LinkedPartNumber: string | null;
  LinkedIsRetired: boolean;
}

let seq = 0;
const nextKey = () => `part-${(seq += 1)}`;

export function toDrafts(components: StockComponent[]): PartDraft[] {
  return components.map((c) => ({
    key: nextKey(),
    ComponentStockItemId: c.ComponentStockItemId,
    // ⚠ The TYPED columns, not the resolved ones. Putting the resolved answer
    //   in the box would write the linked line's name into the typed column
    //   the first time anybody pressed Save, and the link would stop meaning
    //   anything.
    Description: c.TypedDescription ?? '',
    SupplierPartNumber: c.TypedPartNumber ?? '',
    Qty: c.Qty,
    LinkedName: c.LinkedName,
    LinkedPartNumber: c.LinkedPartNumber,
    LinkedIsRetired: c.LinkedIsRetired,
  }));
}

export function toWrite(drafts: PartDraft[]): StockComponentWriteItem[] {
  return drafts.map((d) => ({
    ComponentStockItemId: d.ComponentStockItemId,
    Description: d.Description.trim() || null,
    SupplierPartNumber: d.SupplierPartNumber.trim() || null,
    // ⚠ An emptied box saves as one. The database refuses 0 and the person
    //   plainly meant "one of these" rather than "none of these".
    Qty: typeof d.Qty === 'number' && d.Qty >= 1 ? d.Qty : 1,
  }));
}

/**
 * ⚠ What "changed" means, so an untouched list is not rewritten. A PUT soft
 * deletes every row and inserts the list again, so saving a kit nobody edited
 * would churn the table and fill the audit log with identical entries.
 */
export function sameAsSaved(drafts: PartDraft[], saved: StockComponent[]): boolean {
  if (drafts.length !== saved.length) return false;
  return drafts.every((d, i) => {
    const s = saved[i];
    return d.ComponentStockItemId === s.ComponentStockItemId
      && d.Description.trim() === (s.TypedDescription ?? '')
      && d.SupplierPartNumber.trim() === (s.TypedPartNumber ?? '')
      && (typeof d.Qty === 'number' && d.Qty >= 1 ? d.Qty : 1) === s.Qty;
  });
}

export function blankPart(): PartDraft {
  return {
    key: nextKey(),
    ComponentStockItemId: null,
    Description: '',
    SupplierPartNumber: '',
    Qty: 1,
    LinkedName: null,
    LinkedPartNumber: null,
    LinkedIsRetired: false,
  };
}
